<template>
  <BaseModal
    :modalTitle="$t('area.lab.create.title')"
    :modalIntro="$t('area.lab.create.intro')">
    <UiBaseFormVee
      submitOnCtrlEnter
      :errorText="errorText"
      :onSubmit="onSubmitNewLab">
      <UiInputTextFieldVee
        name="name"
        :label="$t('W: Visningsnavn')"
        :rules="['required']"
        isRequired
        v-model="newLabAccount.name" />
      <UiInputTextFieldVee
        name="labName"
        :label="$t('form.label.company-name')"
        :rules="['required']"
        isRequired
        v-model="newLabAccount.companyDetails.name" />
      <UiInputTextFieldVee
        name="subDomain"
        :label="$t('form.label.sub-domain')"
        :maxLength="12"
        :rules="['required']"
        isRequired
        v-model="newLabAccount.subDomain"
        @input="onSubDomainInput" />
      <UiInputTextFieldVee
        name="address"
        :label="$t('form.label.address')"
        :rules="['required']"
        isRequired
        v-model="newLabAccount.address.addressLine" />
      <div class="flex gap-4">
        <UiInputTextFieldVee
          name="postalCode"
          class="w-1/3"
          :label="$t('form.label.postal-code')"
          :rules="['required']"
          isRequired
          v-model="newLabAccount.address.postalCode" />
        <UiInputTextFieldVee
          name="city"
          class="grow"
          :label="$t('form.label.city')"
          :rules="['required']"
          isRequired
          v-model="newLabAccount.address.city" />
      </div>

      <span class="mt-8 mb-1 text-xs text-text-secondary uppercase">
        {{ $t('common.section.primary-contact') }}
      </span>

      <UiInputTextFieldVee
        name="firstName"
        :label="$t('form.label.first-name')"
        :rules="['required']"
        isRequired
        v-model="newLabAccount.primaryContact.firstName" />
      <UiInputTextFieldVee
        name="lastName"
        :label="$t('form.label.last-name')"
        :rules="['required']"
        isRequired
        v-model="newLabAccount.primaryContact.lastName" />
      <UiInputTextFieldVee
        name="email"
        :label="$t('form.label.email')"
        type="email"
        :rules="['required']"
        isRequired
        v-model="newLabAccount.primaryContact.email" />
      <UiInputPhoneVee
        name="phone"
        ref="phoneField"
        :label="$t('form.label.phone')"
        :rules="['requiredPhone', 'phone']"
        v-model="newLabAccount.primaryContact.phone"
        isRequired />

      <template #buttons>
        <UiBaseButton
          type="submit"
          :text="$t('area.lab.create.action.create')"
          :isLoading="isLoading" />
      </template>
    </UiBaseFormVee>
  </BaseModal>
</template>

<script lang="ts">
import { useUnsavedChangesGuard } from '@/composables/useUnsavedChangesGuard'
import { responseErrors } from '@team-milva/lab-constants'
import { useLabStore } from '@/stores/labStore'
import { useUserStore } from '@/stores/userStore'
import { mapState } from 'pinia'
import { adminApi } from '@/globals/typescript/utils/api/adminClient'
import { useModalStore } from '@/stores/modalStore'
import { useToast } from '@/globals/typescript/utils/toast'
import { LabAccount } from '@/globals/typescript/models/LabAccount'

export default {
  name: 'NewLabModal',
  emits: ['close'],
  data() {
    const newLabAccount = new LabAccount()

    return {
      newLabAccount,
      unsavedGuard: useUnsavedChangesGuard(),

      isLoading: false,
      errorText: '',
    }
  },
  computed: {
    ...mapState(useUserStore, ['currentUser']),
    ...mapState(useLabStore, ['allLabAccounts']),
    hasExistingCompanyName(): boolean {
      return this.allLabAccounts.some(
        (lab) =>
          lab.companyDetails.name.toLowerCase() ===
          this.newLabAccount.companyDetails.name.toLowerCase()
      )
    },
    hasExistingSubDomain(): boolean {
      return this.allLabAccounts.some(
        (lab) => lab.subDomain === this.newLabAccount.subDomain
      )
    },
  },
  watch: {
    newLabAccount: {
      handler() {
        this.errorText = ''
      },
      deep: true,
    },
  },
  methods: {
    onSubDomainInput(event: Event) {
      const input = event.target as HTMLInputElement
      const sanitized = input.value
        .toLowerCase() // Convert to lowercase
        .replace(/[^a-z0-9-]/g, '') // Remove any character that is not a-z, 0-9, or dash
        .replace(/--+/g, '-') // Replace consecutive dashes with a single dash
        .replace(/^-+/, '') // Remove leading dashes
      if (sanitized !== input.value) {
        input.value = sanitized
        this.newLabAccount.subDomain = sanitized
      }
    },
    async onSubmitNewLab() {
      this.errorText = ''

      if (this.hasExistingCompanyName) {
        this.errorText = this.$t('area.lab.create.error.name-exists')
        return
      }
      if (this.hasExistingSubDomain) {
        this.errorText = this.$t('area.lab.create.error.subdomain-exists')
        return
      }

      this.isLoading = true

      try {
        const result = await adminApi('post', '/labs', {
          body: {
            ...this.newLabAccount,
            createdByUserID: this.currentUser?.id ?? '',
          },
        })

        if (result.success) {
          useToast().success(this.$t('area.lab.create.success.created'))
          this.$emit('close')
          return
        }

        if (result.code === responseErrors.ALREADY_EXISTS.code) {
          this.errorText = this.$t('area.lab.create.error.general-exists')
        } else {
          this.errorText = this.$t('error.common.standard')
        }
        useToast().error(this.errorText)
      } finally {
        this.isLoading = false
      }
    },
  },
  created() {
    // Last in created(), after the form is populated
    this.unsavedGuard.register({ modalId: useModalStore().top?.id, snapshot: () => this.newLabAccount })
  },
  beforeUnmount() {
    this.unsavedGuard.unregister()
  },
}
</script>

